import React from 'react';
import {Form, OverlayTrigger, Tooltip} from 'react-bootstrap';
import {IntlShape, useIntl} from 'react-intl';
import Role from '../../classes/api/Role';
import './ModalEditUserRights.module.scss';

interface RoleMeta {
  icon: string;
  color: string;
  bg: string;
  labelId: string;
  subtitleId: string;
  descriptionId: string;
}

const ROLE_META: Record<string, RoleMeta> = {
  administrateur: {
    icon: 'bi-star-fill',
    color: '#f1416c',
    bg: '#fff5f8',
    labelId: 'USER_MANAGER.ROLE.ADMINISTRATEUR.LABEL',
    subtitleId: 'USER_MANAGER.ROLE.ADMINISTRATEUR.SUBTITLE',
    descriptionId: 'USER_MANAGER.ROLE.ADMINISTRATEUR.DESCRIPTION',
  },
  administratif: {
    icon: 'bi-grid-fill',
    color: '#009ef7',
    bg: '#f1faff',
    labelId: 'USER_MANAGER.ROLE.ADMINISTRATIF.LABEL',
    subtitleId: 'USER_MANAGER.ROLE.ADMINISTRATIF.SUBTITLE',
    descriptionId: 'USER_MANAGER.ROLE.ADMINISTRATIF.DESCRIPTION',
  },
  commercial: {
    icon: 'bi-graph-up-arrow',
    color: '#50cd89',
    bg: '#e8fff3',
    labelId: 'USER_MANAGER.ROLE.COMMERCIAL.LABEL',
    subtitleId: 'USER_MANAGER.ROLE.COMMERCIAL.SUBTITLE',
    descriptionId: 'USER_MANAGER.ROLE.COMMERCIAL.DESCRIPTION',
  },
  dataCenter: {
    icon: 'bi-hdd-rack-fill',
    color: '#7239ea',
    bg: '#f8f5ff',
    labelId: 'USER_MANAGER.ROLE.DATA_CENTER.LABEL',
    subtitleId: 'USER_MANAGER.ROLE.DATA_CENTER.SUBTITLE',
    descriptionId: 'USER_MANAGER.ROLE.DATA_CENTER.DESCRIPTION',
  },
  'Super Administrateur': {
    icon: 'bi-people-fill',
    color: '#ffc700',
    bg: '#fff8dd',
    labelId: 'USER_MANAGER.ROLE.SUPER_ADMINISTRATEUR.LABEL',
    subtitleId: 'USER_MANAGER.ROLE.SUPER_ADMINISTRATEUR.SUBTITLE',
    descriptionId: 'USER_MANAGER.ROLE.SUPER_ADMINISTRATEUR.DESCRIPTION',
  },
  technique: {
    icon: 'bi-tools',
    color: '#3f4254',
    bg: '#f4f4f4',
    labelId: 'USER_MANAGER.ROLE.TECHNIQUE.LABEL',
    subtitleId: 'USER_MANAGER.ROLE.TECHNIQUE.SUBTITLE',
    descriptionId: 'USER_MANAGER.ROLE.TECHNIQUE.DESCRIPTION',
  },
  utilisateur_technique: {
    icon: 'bi-person',
    color: '#a1a5b7',
    bg: '#f4f4f4',
    labelId: 'USER_MANAGER.ROLE.UTILISATEUR_TECHNIQUE.LABEL',
    subtitleId: 'USER_MANAGER.ROLE.UTILISATEUR_TECHNIQUE.SUBTITLE',
    descriptionId: 'USER_MANAGER.ROLE.UTILISATEUR_TECHNIQUE.DESCRIPTION',
  },
};

const DEFAULT_ROLE_META: Omit<RoleMeta, 'labelId'> = {
  icon: 'bi-shield-check',
  color: '#a1a5b7',
  bg: '#f4f4f4',
  subtitleId: '',
  descriptionId: '',
};

const SUPER_ADMIN_ROLE_NAME = 'Super Administrateur';

const formatRoleLabel = (name: string): string => {
  const spaced = name.replace(/[_-]/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

const getRoleLabel = (role: Role, intl: IntlShape): string => {
  const meta = ROLE_META[role.getName()];
  return meta ? intl.formatMessage({id: meta.labelId}) : formatRoleLabel(role.getName());
};

// Super Administrateur en premier, puis ordre alphabétique
export const compareRoleNames = (a: string, b: string): number => {
  const aIsSuperAdmin = a === SUPER_ADMIN_ROLE_NAME;
  const bIsSuperAdmin = b === SUPER_ADMIN_ROLE_NAME;
  if (aIsSuperAdmin !== bIsSuperAdmin) {
    return aIsSuperAdmin ? -1 : 1;
  }
  return a.localeCompare(b);
};

const compareRoles = (a: Role, b: Role): number => compareRoleNames(a.getName(), b.getName());

interface RoleSwitchListProps {
  roles: Role[];
  selectedRoles: number[];
  onChange: (roleIds: number[]) => void;
  idPrefix?: string;
}

const RoleSwitchList = ({
  roles,
  selectedRoles,
  onChange,
  idPrefix = 'role',
}: RoleSwitchListProps): JSX.Element => {
  const intl = useIntl();

  return (
    <>
      {[...roles].sort(compareRoles).map((role) => {
        if (!role.isActive()) {
          return null;
        }

        const meta = ROLE_META[role.getName()] ?? DEFAULT_ROLE_META;
        const label = getRoleLabel(role, intl);
        const subtitle = meta.subtitleId ? intl.formatMessage({id: meta.subtitleId}) : '';
        const description = meta.descriptionId ? intl.formatMessage({id: meta.descriptionId}) : '';

        return (
          <div
            className="d-flex align-items-center justify-content-between py-2"
            key={`roles-${role.getId()}`}
          >
            <div className="d-flex align-items-center">
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 10,
                  marginRight: 12,
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: meta.bg,
                  color: meta.color,
                }}
              >
                <i className={`bi ${meta.icon}`} style={{fontSize: 18}} />
              </div>
              <div>
                {role.getName() === SUPER_ADMIN_ROLE_NAME ? (
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip id={`role-former-name-${role.getId()}`}>
                        {intl.formatMessage({
                          id: 'USER_MANAGER.ROLE.SUPER_ADMINISTRATEUR.FORMER_NAME',
                        })}
                      </Tooltip>
                    }
                  >
                    <div className="fw-bold text-gray-800" style={{cursor: 'pointer'}}>
                      {label}
                    </div>
                  </OverlayTrigger>
                ) : (
                  <div className="fw-bold text-gray-800">{label}</div>
                )}
                {subtitle && <div className="text-muted fs-7">{subtitle}</div>}
              </div>
            </div>

            <div className="d-flex align-items-center">
              {description && (
                <OverlayTrigger
                  placement="left"
                  overlay={<Tooltip id={`role-desc-${role.getId()}`}>{description}</Tooltip>}
                >
                  <span style={{marginRight: 10, cursor: 'pointer'}} className="text-muted">
                    <i className="bi bi-info-circle" />
                  </span>
                </OverlayTrigger>
              )}
              <Form.Check
                type="switch"
                id={`${idPrefix}-${role.getId()}`}
                value={role.getId()}
                checked={selectedRoles.includes(role.getId())}
                onChange={(event) =>
                  onChange(
                    event.target.checked
                      ? [...selectedRoles, role.getId()]
                      : selectedRoles.filter((roleId) => roleId !== role.getId())
                  )
                }
              />
            </div>
          </div>
        );
      })}
    </>
  );
};

export default RoleSwitchList;
